import FormControl from 'common/components/FormControl/FormControl';
import FormScrollError from 'common/components/FormScrollError/FormScrollError';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import arrayMutators from 'final-form-arrays';
import useRouter from 'common/hooks/use-router';
import EditHeader from 'common/components/EditHeader/EditHeader';
import SectionTitle from 'common/components/SectionTitle/SectionTitle';
import HeaderButtonsEdit from 'common/components/EditHeader/HeaderButtonsEdit';
import fixFinalFormEmptyValues from 'utils/fixFinalFormEmptyValues';
import dayjs from 'dayjs';
import Icon from 'components/Icon/Icon';
import { isMailCorrect } from 'common/components/Password/Password';
import { isPasswordCorrect } from 'common/components/Password/JPassword';
import useItem from 'common/hooks/useItem';
import Button from 'common/components/Button/Button';
import DepartmentSelect from 'components/select/DepartmentSelect/DepartmentSelect';
import EstablishmentSelect from 'components/select/EstablishmentSelect/EstablishmentSelect';
import { XCircleIcon, MinusCircleIcon } from '@heroicons/react/24/outline';
import { Operator } from 'types/Operator';
import fetchJSON from 'common/utils/fetchJSON';
import AlertsContext from 'common/providers/alerts';

// Interface pour les valeurs temporaires du formulaire
interface TempFormValues {
  department?: number;
  establishment?: number;
}

// Interface pour les valeurs complètes du formulaire
interface MediatorFormValues extends Operator {
  temp?: TempFormValues;
}

function MediatorForm({ onSubmit, editedUser, myAccount }: any) {
  const { t } = useTranslation();
  const { navigate } = useRouter();
  const { setAlert } = useContext(AlertsContext);
  const [changePassword, setChangePassword] = useState(false);
  const [establishments, setEstablishments] = useState<any[]>(
    editedUser?.operator?.establishments || []
  );
  const [showAddForm, setShowAddForm] = useState(false);

  const checkEmail = (value: string) => (isMailCorrect(value) ? undefined : t('forms.badEmail'));
  const checkPassword = (value: string) =>
    isPasswordCorrect(value) ? undefined : t('common.passwordTooWeak');

  const editedOperator = editedUser?.operator;

  const { updateItem, error, isFetching }: any = useItem('users', editedUser?.id);

  useEffect(() => {
    if (error) {
      setAlert(error.message || t('common.error'), 'danger');
    }
  }, [error, setAlert, t]);

  const changePasswordHandler = useCallback(
    async (userData: any) => {
      const user = { ...userData };

      try {
        await updateItem(user.id, user, false);

        setChangePassword(false);
      } catch (e: any) {
        return e;
      }
      return true;
    },
    [updateItem]
  );

  const initialValues: MediatorFormValues = useMemo(
    () =>
      editedOperator?.id
        ? {
            ...editedOperator,
            role: editedOperator?.user?.role?.id ?? 1,
            role_name: editedOperator?.user?.role?.name,
            user: editedUser,
          }
        : {
            username: undefined,
            role: editedOperator?.user?.role?.id,
            role_name: editedOperator?.user?.role?.name,
            user: editedUser,
          },
    [editedOperator]
  );

  const onSubmitWrapper = async (values: MediatorFormValues) => {
    const updatedValues = {
      ...fixFinalFormEmptyValues(initialValues, values),
      establishments: establishments || [],
    };
    const res = await onSubmit(updatedValues);

    return res;
  };

  // Fonction pour ajouter un établissement
  const addEstablishment = async (establishmentId: number) => {
    if (!establishmentId) return;

    // Vérifier si l'établissement existe déjà
    const exists = establishments.some((est) => est.id === establishmentId);
    if (!exists) {
      // get establishment full entity
      const res = await fetchJSON({
        method: 'GET',
        url: `establishments/${establishmentId}?populate=department`,
      });
      if (res?.data) {
        setEstablishments([
          ...establishments,
          {
            id: res?.data?.id,
            name: res?.data?.attributes?.name,
          },
        ]);

        setShowAddForm(false);
      }
    }
  };

  // Fonction pour supprimer un établissement
  const removeEstablishment = (establishmentId: number) => {
    const newEstablishments = establishments.filter((est) => est.id !== establishmentId);
    setEstablishments(newEstablishments);
  };

  return (
    <Form
      onSubmit={onSubmitWrapper}
      mutators={{
        ...arrayMutators,
      }}
      subscription={{ values: true, submitting: true, pristine: true }}
      initialValues={initialValues}
      render={({ handleSubmit, values, form }) => {
        // Utiliser l'interface MediatorFormValues pour les valeurs du formulaire
        const formValues = values as MediatorFormValues;

        return (
          <div className="mx-auto max-w-full py-6 px-6 text-slate-500 text-base">
            {/* Header */}
            <EditHeader
              title={
                myAccount
                  ? `${t('titlePages.myAccount')}`
                  : editedOperator?.id
                  ? t('editItem.operator')
                  : t('newItem.operator')
              }
              subTitle={
                editedOperator?.id
                  ? `${formValues.username} - ${formValues.firstname || ''} ${
                      formValues.lastname || ''
                    }`
                  : t('newItem.operatorLegend')
              }
              icon="UsersIcon"
              HeaderButtonsEdit={
                <HeaderButtonsEdit
                  inHeader
                  onSubmit={handleSubmit}
                  onCancel={
                    myAccount ? () => navigate('/my-account-edit') : () => navigate('/mediators')
                  }
                />
              }
            />
            <form onSubmit={handleSubmit} noValidate autoComplete="off">
              <FormScrollError />

              <div className="responsive-flex gap-6 mt-2">
                <div className="md:w-2/3 flex flex-col gap-6">
                  <div className="white-box">
                    <div className="responsive-flex gap-4">
                      <div className="flex flex-col w-full md:w-[180px] justify-center">
                        <Icon
                          name="Mediator"
                          className="w-full text-center w-24 h-24 rounded-sm p-1 mr-0.5 text-slate-500"
                        />
                        <div className="mt-1 mb-4 flex items-center justify-center gap-1 leading-[0] text-slate-500 shrink-0 font-normal pb-1">
                          {t('roles.Mediator')}
                        </div>
                        {editedOperator?.id ? (
                          <div className="text-xs text-center w-full px-1 pt-0 pb-4">
                            {t('forms.subscribedThe', {
                              since: dayjs(formValues?.createdAt).format('DD/MM/YYYY'),
                            })}
                          </div>
                        ) : null}
                      </div>
                      <div className="flex flex-col w-full gap-2">
                        <FormControl name="username" titleKey="forms.username" required />
                        <div className="grid grid-cols-2 w-full gap-2">
                          <FormControl name="firstname" titleKey="forms.firstname" required />
                          <FormControl name="lastname" titleKey="forms.lastname" required />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="white-box flex flex-col gap-4">
                    <div className="responsive-flex flex-col gap-4">
                      <SectionTitle title={t('forms.establishments')} icon="CalendarDaysIcon" />

                      {/* Liste des établissements */}
                      <div className="flex flex-col w-full gap-2">
                        {establishments.length > 0 ? (
                          establishments.map((establishment) => (
                            <div
                              key={establishment.id}
                              className="flex items-center justify-between p-2 border border-slate-200 rounded-lg"
                            >
                              <div className="flex-1 font-bold">
                                {`${establishment.name}`}
                                {establishment.department?.CP &&
                                  ` (${String(establishment.department?.CP).padStart(2, '0')})`}
                              </div>
                              <button
                                type="button"
                                className="p-1 text-slate-500 hover:text-red-500 rounded-full"
                                onClick={() => removeEstablishment(establishment.id)}
                              >
                                <MinusCircleIcon className="w-5 h-5" />
                              </button>
                            </div>
                          ))
                        ) : (
                          <div className="text-slate-400 italic p-2">
                            {t('forms.noEstablishments', 'Aucun établissement')}
                          </div>
                        )}
                      </div>

                      {/* Formulaire d'ajout d'établissement */}
                      {!showAddForm ? (
                        <div className="flex flex-row justify-end w-full">
                          <Button
                            type="primary"
                            icon="PlusCircleIcon"
                            onClick={() => {
                              form.change('temp', {});
                              setShowAddForm(true);
                            }}
                            title={t('actions.addEstablishment')}
                            className="my-auto  rounded-lg"
                            small
                          />
                        </div>
                      ) : (
                        <div className="border border-slate-200 p-3 rounded-lg">
                          <div className="flex justify-between items-center mb-2">
                            <SectionTitle
                              title={t('actions.addEstablishment')}
                              icon="PlusCircleIcon"
                              small
                            />
                            <button
                              type="button"
                              className="p-1 text-slate-500 hover:text-red-500"
                              onClick={() => setShowAddForm(false)}
                            >
                              <XCircleIcon className="w-5 h-5" />
                            </button>
                          </div>
                          <div className="flex flex-col gap-3 mx-4">
                            <FormControl
                              title={t('forms.department')}
                              labelIcon="IoLocationOutline"
                              type="custom"
                              name="temp.department"
                              inputContainerStyle="w-full"
                              className="w-full"
                              containerStyle="w-full"
                            >
                              <DepartmentSelect />
                            </FormControl>

                            <FormControl
                              title={t('forms.establishment')}
                              labelIcon="EstablishmentIcon"
                              type="custom"
                              name="temp.establishment"
                              inputContainerStyle="w-full"
                              className="w-full"
                              containerStyle="w-full"
                              onChange={(establishmentId) => {
                                addEstablishment(establishmentId);
                              }}
                            >
                              <EstablishmentSelect
                                filterOn={{
                                  department: formValues.temp?.department,
                                  id: {
                                    $notIn: establishments.map((e) => e.id),
                                  },
                                }}
                                excludeIds={establishments.map((e) => e.id)}
                              />
                            </FormControl>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex flex-col md:w-1/3 gap-6">
                  <div className="white-box">
                    <div className="flex flex-col w-full gap-2">
                      <SectionTitle title={t('forms.authentication')} icon="CalendarDaysIcon" />

                      <FormControl
                        name="user.email"
                        titleKey="forms.email"
                        required
                        validate={checkEmail}
                        disabled={!!formValues?.user}
                      />
                    </div>
                  </div>
                  {!changePassword && formValues?.id && (
                    <div className="w-full flex flex-row justify-center gap-2">
                      <Button
                        type="primary-line"
                        onClick={() => {
                          formValues.user.password = '';
                          (formValues.user as any).confirmPassword = '';
                          setChangePassword(true);
                        }}
                        title={t('actions.changePassword')}
                        className="my-auto py-4"
                      />
                    </div>
                  )}
                  {changePassword && (
                    <div className="white-box flex flex-col gap-4">
                      <FormControl
                        type="jpassword"
                        name="user.password"
                        titleKey="forms.password"
                        required
                        validate={checkPassword}
                      />
                      <FormControl
                        name="user.confirmPassword"
                        title={t('forms.confirmPassword')}
                        type="password"
                      />

                      <div className="w-full flex flex-row justify-center gap-2">
                        <Button
                          type="neutral"
                          onClick={() => setChangePassword(false)}
                          title={t('actions.cancel')}
                          className="justify-center py-4"
                        />
                        <Button
                          type="primary"
                          onClick={() => changePasswordHandler(formValues?.user)}
                          title={t('actions.changePassword')}
                          className="justify-center py-4"
                          loading={isFetching}
                        />
                      </div>
                      {t('common.passwordInstruction')}
                    </div>
                  )}
                </div>
              </div>
            </form>
          </div>
        );
      }}
    />
  );
}

export default MediatorForm;
